// Pin and hide are a view preference: they change the order of this person's
// board, not the account. GRO-138's available table should set the same `group`
// field rather than inventing a second grouping pass.
//
// A booked meeting is not a preference. The worklist is for accounts that still
// need outreach; once a sitting is on the calendar (or already happened) the
// account has moved to the AE. Pin and hide still win — a booked account
// someone pinned stays at the top, and a hidden one stays struck through at
// the bottom.
//
// "Booked", never "held": the mirror cannot tell a sitting from a no-show
// (GRO-143). `meetings` is all-time owned sittings; `next_meeting` is the
// upcoming one. Either is enough. This function is the one predicate the
// grouping pass and the Meeting booked filter share.
//
// Auto is an account the auto lane sequenced from this strategist's mailbox
// that nobody there has really replied to yet. The server decides it
// (`auto_lane` on the row, auto_send/lane_accounts.py); this only places it.
// It sits below To work so it never mixes with hand-worked accounts, and a
// real reply moves it up into To work. Pin, hide and a booked meeting all
// win over it, the same way they win over To work: a pinned or booked Auto
// row keeps its Auto pill wherever it lands.

import type { BookAccount } from '../api/types.ts';
import type { RankedAccount } from './derived.ts';

export function meetingBooked(row: BookAccount): boolean {
  return (row.meetings ?? 0) > 0 || Boolean(row.next_meeting);
}

export function isAutoLane(row: BookAccount): boolean {
  return Boolean(row.auto_lane);
}

export type BoardPref = 'pinned' | 'hidden' | null;
export type BoardPrefsMap = Record<string, 'pinned' | 'hidden'>;

// Section order, top to bottom: Pinned, To work, Auto, Meeting booked, Hidden.
export const GROUP_PINNED = 0;
export const GROUP_BODY = 1;
export const GROUP_AUTO = 2;
export const GROUP_BOOKED = 3;
export const GROUP_HIDDEN = 4;

// The sticky divider's label for each section.
export const GROUP_LABELS: Record<number, string> = {
  [GROUP_PINNED]: 'Pinned',
  [GROUP_BODY]: 'To work',
  [GROUP_AUTO]: 'Auto',
  [GROUP_BOOKED]: 'Meeting booked',
  [GROUP_HIDDEN]: 'Hidden',
};

// The Auto pill's hover text: whose mailbox sent it, when, and what moves it to To work.
export function autoLaneTitle(row: BookAccount): string | null {
  const lane = row.auto_lane;
  if (!lane) return null;
  return `Scout's auto lane sequenced this from ${lane.sender_email} on ${lane.sent_on}. `
    + 'It moves to To work when someone there replies (out-of-office and bounces do not count).';
}

export function attachBoardPrefs(
  rows: RankedAccount[],
  prefs: BoardPrefsMap | null | undefined,
): RankedAccount[] {
  return rows.map(row => {
    const pref = prefs?.[row.id] ?? null;
    if (pref === 'pinned') {
      return { ...row, board_pref: 'pinned', group: GROUP_PINNED };
    }
    if (pref === 'hidden') {
      return { ...row, board_pref: 'hidden', group: GROUP_HIDDEN };
    }
    if (meetingBooked(row)) {
      return { ...row, board_pref: null, group: GROUP_BOOKED };
    }
    if (isAutoLane(row)) {
      return { ...row, board_pref: null, group: GROUP_AUTO };
    }
    return { ...row, board_pref: null, group: GROUP_BODY };
  });
}

export function nextPreference(current: BoardPref, action: 'pinned' | 'hidden'): BoardPref {
  if (current === action) return null;
  return action;
}

// The line the board shows after a pin or hide, so the row jumping groups is never silent.
// `previous` is what the row was before; a failed write names what did not happen.
export function boardPrefNote(account: string, previous: BoardPref, next: BoardPref, failed: boolean): string {
  if (failed) {
    const verb = next === 'pinned' ? 'pin' : next === 'hidden' ? 'hide' : previous === 'hidden' ? 'unhide' : 'unpin';
    return `Could not ${verb} ${account}; it is back where it was. Try again.`;
  }
  if (next === 'hidden') return `Hid ${account}. It is under Hidden at the bottom of your board; Unhide brings it back.`;
  if (next === 'pinned') return `Pinned ${account} to the top of your board.`;
  if (previous === 'hidden') return `${account} is back on your board.`;
  return `Unpinned ${account}.`;
}
